import { faChevronDown, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence } from "motion/react";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { mergeProps } from "react-aria";
import { FormProvider } from "react-hook-form";

import { selectedKeysFromFormValue } from "../../utils/misc";
import { mergeRefs } from "../../utils/refs";
import { Button } from "../button/Button";
import { ScrollShadow } from "../extra/ScrollShadow";
import { useFormItemContext } from "../form/FormItem";
import { useControllerSafe } from "../form/useControllerSafe";
import { Input } from "../input/Input";
import { ListBox } from "../listbox/ListBox";
import StandalonePopover from "../popover/StandalonePopover";
import { MultiSelectTags } from "../tag/MultiSelectTags";
import { useComboBox } from "./useComboBox";

import type { ForwardedRef, ReactElement, ReactNode } from "react";
import type { Key } from "react-stately";
import type { UseComboBoxProps } from "./useComboBox";

// ComboBox renders an input internally but we don't want it to hook into the form context
// since we're already managing the form state ourselves. We block the inner input from seeing
// the actual form context by inserting a blank one in between
const FormContextBarrier = (props: { children: ReactNode }) => {
  return <FormProvider {...(props as any)} />;
};

export type ComboBoxProps<T> = UseComboBoxProps<T>;

const ComboBoxImpl = <T extends object>(
  props: ComboBoxProps<T>,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const formController = useControllerSafe({ name: props.name ?? "" });
  // Check if we're inside a Form.Item - if so, skip our own error display and default to fullWidth
  const isInsideFormItem = useFormItemContext();

  // When inside a Form.Item, default to fullWidth unless explicitly set to false
  const effectiveFullWidth = isInsideFormItem && props.fullWidth !== false ? true : props.fullWidth;

  if (formController && !props.name) {
    throw new Error("ComboBox used inside a Form component must have a name prop");
  }

  const { error: formError, invalid: formInvalid } = formController?.fieldState ?? {};

  const formComboBoxProps = useMemo(
    () =>
      formController?.field
        ? {
            onSelectionChange: (keys: Set<Key>) => {
              const keysArray = Array.from(keys);
              const value = props.selectionMode === "multiple" ? keysArray : keysArray[0];
              formController.field.onChange(value);
            },
            selectedKeys: selectedKeysFromFormValue(
              formController.field.value,
              props.selectionMode ?? "single"
            ),
            name: formController.field.name,
            onClose: formController.field.onBlur,
            isDisabled: formController.field.disabled,
            isInvalid: formInvalid,
            // Don't pass errorMessage when inside Form.Item - let Form.Item handle error display
            errorMessage: isInsideFormItem ? undefined : formError?.message,
          }
        : undefined,
    [formController?.field, formError, formInvalid, props.selectionMode, isInsideFormItem]
  );

  const {
    inputRef,
    inputWrapperRef,
    popoverRef,
    isOpen,
    disableAnimation,
    allowsCustomValue,
    selectionMode,
    state,
    slots,
    endContent,
    baseProps,
    selectorButtonProps,
    clearButtonProps,
    inputProps,
    listBoxProps,
    listBoxWrapperProps,
    popoverProps,
    endContentWrapperProps,
    tagGroupProps,
  } = useComboBox<T>({
    ...(formComboBoxProps ? mergeProps(props, formComboBoxProps) : props),
    fullWidth: effectiveFullWidth,
    ref: formController?.field ? mergeRefs(ref, formController.field.ref) : ref,
  });

  // apply the same width to the popover as the select
  useEffect(() => {
    if (isOpen && popoverRef.current && inputWrapperRef.current) {
      const rect = inputWrapperRef.current.getBoundingClientRect();

      const popover = popoverRef.current;

      popover.style.width = rect.width + "px";
    }
  }, [isOpen]);

  // unfocus the input when the popover closes & there's no selected item & no allows custom value
  useEffect(() => {
    if (!isOpen && !state.selectedItems?.length && inputRef.current && !allowsCustomValue) {
      // Check if we're inside a modal or focus trap that would immediately refocus the input
      // If so, avoid blurring to prevent infinite focus/blur cycles
      const isInModal = inputRef.current.closest('[role="dialog"]') !== null;
      if (!isInModal) {
        inputRef.current.blur();
      }
    }
  }, [isOpen, state.selectedItems?.length, inputRef, allowsCustomValue]);

  const popoverContent = isOpen ? (
    <StandalonePopover {...popoverProps} state={state}>
      <ScrollShadow {...listBoxWrapperProps}>
        <ListBox {...listBoxProps} />
      </ScrollShadow>
    </StandalonePopover>
  ) : null;

  // There is a render cycle in between when an item is added and when the tag shows up in the
  // combobox - we track this explicitly based on the visibility of the tags to avoid the
  // placeholder flickering when the first tag is added
  const [multiSelectTagsVisible, setMultiSelectTagsVisible] = useState(false);

  const showPlaceholder =
    state.inputValue.length === 0 &&
    (selectionMode === "multiple" ? !multiSelectTagsVisible : state.selectedKeys.size === 0);
  const showSelectedValue = state.inputValue.length === 0;

  const input = (
    <Input
      {...inputProps}
      fullWidth={effectiveFullWidth}
      endContent={
        <div {...endContentWrapperProps}>
          {endContent || (
            <Button {...clearButtonProps}>
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          )}
          <Button {...selectorButtonProps}>
            <FontAwesomeIcon icon={faChevronDown} />
          </Button>
        </div>
      }
    >
      {showPlaceholder && (
        <span className={slots.placeholder({ className: "block w-full truncate text-left pr-6" })}>
          {inputProps.placeholder}
        </span>
      )}
      {selectionMode === "multiple" ? (
        <MultiSelectTags
          {...tagGroupProps}
          selectAll={props.selectAll}
          /**
           * We want the input to only take up as much space as it needs so that the tag list can
           * fill the rest of the available space. To accomplish this we have a hidden span tag
           * that will drive the width of the input element based on the current text value.
           */
          endContent={
            <div className={slots.inputWrapper()}>
              <span className={slots.inputWidthSetter()}>{state.inputValue}</span>
              <Input.Input />
            </div>
          }
          onVisibleChange={count => setMultiSelectTagsVisible(count > 0)}
        />
      ) : (
        <div className={slots.inputWrapper({ className: "flex flex-grow" })}>
          <Input.Input />
          {showSelectedValue && (
            <span className={slots.selectedValue()}>{state.selectedItems?.[0]?.textValue}</span>
          )}
        </div>
      )}
    </Input>
  );

  return (
    <div {...baseProps}>
      {formController ? <FormContextBarrier>{input}</FormContextBarrier> : input}
      {disableAnimation ? popoverContent : <AnimatePresence>{popoverContent}</AnimatePresence>}
    </div>
  );
};

// Need to do this manually to get the generic type which doesn't play super well with forwardRef
const WrappedComboBox = forwardRef(ComboBoxImpl) as <T = object>(
  props: ComboBoxProps<T> & { ref?: ForwardedRef<HTMLInputElement> }
) => ReactElement<unknown>;

/**
 * A text input combined with a selection menu, allowing users to filter longer lists to only the
 * options matching a query.
 */
export const ComboBox = WrappedComboBox as typeof WrappedComboBox & {
  displayName: "ComboBox";
  Item: typeof ListBox.Item;
  Section: typeof ListBox.Section;
};
ComboBox.displayName = "ComboBox";
ComboBox.Item = ListBox.Item;
ComboBox.Section = ListBox.Section;

export type { ListBoxItemProps as ComboBoxItemProps } from "../listbox/ListBoxItem";
export type { ListBoxSectionProps as ComboBoxSectionProps } from "../listbox/ListBoxSection";
